/**
 * Create Or Edit Connector Tool Schema
 * 
 * Create a new connector line or edit an existing connector on a slide.
 */

import { z } from "zod";

// Import shared schemas
import {
    PointSchema,
    ArrowheadSchema,
} from "../../../tools/liveBuildArea/liveBuildArea.schema";
import { MetadataOperationsSchema } from "../createOrEditShape/createOrEditShape.schema";

// ═══════════════════════════════════════════════════════════════════════════
// CONNECTOR CONFIG SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Line style schema
 */
export const LineStyleSchema = z.object({
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional()
        .describe("Line color (#RRGGBB)"),
    weight: z.number().min(0.25).optional()
        .describe("Line weight in points"),
    dashStyle: z.enum(["solid", "dash", "dot", "dashDot", "longDash"]).optional()
        .describe("Dash style"),
});

/**
 * Connector config schema
 */
export const ConnectorConfigSchema = z.object({
    type: z.enum(["straight", "elbow", "curved"])
        .describe("Connector type"),
    
    points: z.array(PointSchema)
        .min(2)
        .describe("Array of points (minimum 2)"),
    
    lineStyle: LineStyleSchema.optional()
        .describe("Line styling"),
    
    arrowheadStart: ArrowheadSchema.optional()
        .describe("Arrowhead at start point"),
    
    arrowheadEnd: ArrowheadSchema.optional()
        .describe("Arrowhead at end point"),
});

// ═══════════════════════════════════════════════════════════════════════════
// MAIN CREATE OR EDIT CONNECTOR PARAMS SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create Or Edit Connector parameters schema
 */
export const CreateOrEditConnectorParamsSchema = z.object({
    slideId: z.number().int().min(1)
        .describe("1-based slide index"),
    
    operationType: z.enum(["create", "edit"])
        .describe("'create' for new connector, 'edit' for existing connector"),
    
    shapeId: z.number().int().optional()
        .describe("Connector shape ID (REQUIRED when operationType='edit')"),
    
    purpose: z.string().optional()
        .describe("Documentation: what this connector is for"),
    
    notes: z.string().optional()
        .describe("Additional notes"),
    
    // Connector config
    connectorConfig: ConnectorConfigSchema
        .describe("Connector configuration"),
    
    // Metadata (optional)
    metadata: MetadataOperationsSchema.optional()
        .describe("Unified metadata (edit mode: removeEntryIds + addEntries)"),
}).refine(
    (data) => {
        // shapeId required for edit
        if (data.operationType === "edit" && !data.shapeId) {
            return false;
        }
        return true;
    },
    {
        message: "shapeId required for edit mode",
    }
);

export type CreateOrEditConnectorParams = z.infer<typeof CreateOrEditConnectorParamsSchema>;
export type ConnectorConfig = z.infer<typeof ConnectorConfigSchema>;
export type LineStyle = z.infer<typeof LineStyleSchema>;

